import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  RefreshCw,
  Search as SearchIcon,
  Image as ImageIcon,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

const FILTERS = [
  { key: "all", label: "All" },
  { key: "gear", label: "Gear" },
  { key: "accessories", label: "Accessories" },
  { key: "program", label: "Program" },
  { key: "unclassified", label: "Unclassified" },
  { key: "featured", label: "Featured" },
  { key: "new_release", label: "New release" },
  { key: "best_seller", label: "Best seller" },
  { key: "same_day_eligible", label: "Same-day" },
  { key: "missing_image", label: "Missing image" },
  { key: "source_missing", label: "Missing from source" },
];

function fmtUsd(cents) {
  if (cents === null || cents === undefined) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

export default function AdminShopifyMigration() {
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        fetch(`${API}/api/admin/shopify/migration/status`, { credentials: "include" }),
        fetch(`${API}/api/admin/shopify/products`, { credentials: "include" }),
      ]);
      const s = sRes.ok ? await sRes.json() : null;
      const p = pRes.ok ? await pRes.json() : [];
      setStatus(s);
      setProducts(Array.isArray(p) ? p : []);
    } catch {
      toast.error("Could not load migration page.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Products now come from /api/admin/shopify/products — already in the raw
  // shopify_products shape, so we just expose the fields used by the grid.
  const enriched = useMemo(() => {
    return products.map((p) => ({
      handle: p.handle,
      title: p.title,
      image: p.image,
      media: p.media || [],
      price_cents: p.price_min_cents ?? null,
      section: p.section || null,
      featured: !!p.featured,
      new_release: !!p.new_release,
      best_seller: !!p.best_seller,
      same_day_eligible: !!p.same_day_eligible,
      collection_slug:
        p.section === "gear"
          ? "lifted-gear"
          : p.section === "accessories"
          ? "lifted-accessories"
          : p.section === "program"
          ? "affiliate-program"
          : null,
      vendor: p.vendor,
      missing_image: !p.image,
      source_missing: !!p.source_missing,
    }));
  }, [products]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return enriched.filter((p) => {
      // search
      if (term) {
        const hay = `${p.title} ${p.handle} ${p.vendor || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      // filter
      switch (filter) {
        case "all":
          return true;
        case "gear":
          return p.section === "gear";
        case "accessories":
          return p.section === "accessories";
        case "program":
          return p.section === "program";
        case "unclassified":
          return !p.section || p.section === "unclassified";
        case "featured":
          return p.featured;
        case "new_release":
          return p.new_release;
        case "best_seller":
          return p.best_seller;
        case "same_day_eligible":
          return p.same_day_eligible;
        case "missing_image":
          return p.missing_image;
        case "source_missing":
          return p.source_missing;
        default:
          return true;
      }
    });
  }, [enriched, filter, search]);

  async function resync() {
    setSyncing(true);
    toast.loading("Re-syncing from liftedatthemoment.com…", { id: "resync" });
    try {
      const r = await fetch(`${API}/api/admin/shopify/migrate`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const body = await r.json();
      toast.success(
        `Synced ${body.products_seen} seen · ${body.products_created} new · ${body.products_updated} updated · ${body.products_missing} missing`,
        { id: "resync" }
      );
      await fetchAll();
    } catch (e) {
      toast.error("Re-sync failed — check backend logs.", { id: "resync" });
    } finally {
      setSyncing(false);
    }
  }

  async function patchProduct(handle, updates) {
    try {
      const r = await fetch(`${API}/api/admin/shopify/products/${handle}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(updates),
      });
      if (!r.ok) throw new Error(`${r.status}`);
      // Refresh from server to get the truth
      await fetchAll();
      toast.success("Saved.");
    } catch {
      toast.error("Could not save.");
    }
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl" data-testid="admin-shopify-migration">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-2">
            Legacy reference · Read-only
          </div>
          <h1 className="font-anton uppercase text-4xl text-white">
            Imported from Shopify (Legacy)
          </h1>
          <p className="text-lifted-dim text-sm mt-2 max-w-2xl">
            Original source: Shopify. <strong className="text-white">No active Shopify
            sync is required.</strong> Lifted Core is now the System of Record — use the
            <Link to="/admin/inventory" className="text-lifted-green underline mx-1">Lifted Inventory</Link>
            editor for all live product changes. This screen exists only for
            historical reference and re-import in case of disaster recovery.
          </p>
        </div>
        <button
          onClick={resync}
          disabled={syncing}
          className="px-5 py-3 font-bebas uppercase tracking-wider bg-white/10 text-white/70 rounded inline-flex items-center gap-2 hover:bg-white/15 disabled:opacity-50"
          data-testid="resync-btn"
          title="Legacy: re-import from Shopify only if disaster recovery is needed"
        >
          {syncing ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <RefreshCw size={16} />
          )}
          {syncing ? "Re-importing…" : "Re-import (legacy)"}
        </button>
      </div>

      {/* Schedule panel — nightly re-crawl status */}
      {status && (
        <div className="glass-panel p-5 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="schedule-panel">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-lifted-dim">
              Nightly scheduler
            </div>
            <div
              className={`font-anton text-xl mt-1 ${
                status.scheduler_enabled ? "text-lifted-green" : "text-amber-300"
              }`}
              data-testid="scheduler-enabled"
            >
              {status.scheduler_enabled ? "Enabled" : "Disabled"}
            </div>
            <div className="text-[10px] text-lifted-dim mt-0.5">
              {status.scheduler_enabled
                ? `Daily at ${String(status.scheduler_hour_utc).padStart(2, "0")}:00 UTC`
                : "SHOPIFY_RECRAWL_ENABLED=false"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-lifted-dim">
              Next scheduled run
            </div>
            <div className="font-anton text-xl text-white mt-1" data-testid="next-run">
              {status.next_scheduled_run_at
                ? new Date(status.next_scheduled_run_at).toLocaleString()
                : "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-lifted-dim">
              Last nightly run
            </div>
            <div className="font-anton text-xl text-white mt-1" data-testid="last-nightly-run">
              {status.last_nightly_run
                ? new Date(status.last_nightly_run.started_at).toLocaleString()
                : "Never"}
            </div>
            {status.last_nightly_run && (
              <div className="text-[10px] text-lifted-dim mt-0.5">
                {status.last_nightly_run.products_created} new ·{" "}
                {status.last_nightly_run.products_updated} updated ·{" "}
                {status.last_nightly_run.products_missing} missing
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-lifted-dim">
              Last manual run
            </div>
            <div className="font-anton text-xl text-white mt-1" data-testid="last-manual-run">
              {status.last_manual_run
                ? new Date(status.last_manual_run.started_at).toLocaleString()
                : "Never"}
            </div>
            {status.last_manual_run && (
              <div className="text-[10px] text-lifted-dim mt-0.5">
                {status.last_manual_run.products_created} new ·{" "}
                {status.last_manual_run.products_updated} updated ·{" "}
                {status.last_manual_run.products_missing} missing
              </div>
            )}
          </div>
        </div>
      )}

      {/* Status row */}
      {status && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-testid="migration-status">
          <Stat label="Products" value={status.products_total} />
          <Stat label="Collections" value={status.collections_total} />
          <Stat label="Gear" value={status.gear_total} />
          <Stat label="Accessories" value={status.accessories_total} />
          <Stat label="Program" value={status.program_total} />
          <Stat
            label="Unclassified"
            value={status.unclassified_total}
            tone={status.unclassified_total > 0 ? "warn" : "ok"}
          />
          <Stat
            label="Missing"
            value={status.missing_total}
            tone={status.missing_total > 0 ? "warn" : "ok"}
          />
          <Stat
            label="Last run"
            value={status.last_run?.elapsed_seconds ? `${status.last_run.elapsed_seconds}s` : "—"}
            sub={
              status.last_run?.run_type
                ? `${status.last_run.run_type} · ${status.last_run.started_at?.slice(0, 19)?.replace("T", " ")}`
                : status.last_run?.started_at?.slice(0, 19)?.replace("T", " ")
            }
          />
        </div>
      )}

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative">
          <SearchIcon
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
          />
          <input
            type="text"
            placeholder="Search title / handle / vendor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-lifted-green w-64"
            data-testid="migration-search"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
              className={`px-3 py-1.5 text-[10.5px] uppercase tracking-wider font-bebas rounded border ${
                filter === f.key
                  ? "bg-lifted-green/25 border-lifted-green text-lifted-green"
                  : "bg-black/30 border-white/10 text-lifted-dim hover:border-white/30"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-lifted-dim ml-auto">
          {filtered.length} of {enriched.length} product(s)
        </span>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center gap-2 text-lifted-dim p-8">
          <Loader2 className="animate-spin" size={16} /> Loading products…
        </div>
      ) : filtered.length === 0 ? (
        <div className="glass-panel p-8 text-lifted-dim">No products match this filter.</div>
      ) : (
        <div className="overflow-x-auto glass-panel" data-testid="products-grid">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-wider text-lifted-dim border-b border-white/10">
              <tr>
                <th className="p-3 font-normal w-14">Image</th>
                <th className="p-3 font-normal">Title</th>
                <th className="p-3 font-normal">Handle</th>
                <th className="p-3 font-normal">Price</th>
                <th className="p-3 font-normal">Section</th>
                <th className="p-3 font-normal">Feat</th>
                <th className="p-3 font-normal">New</th>
                <th className="p-3 font-normal">Best</th>
                <th className="p-3 font-normal">Same-day</th>
                <th className="p-3 font-normal">Collection</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((p) => (
                <tr key={p.handle} data-testid={`product-row-${p.handle}`}>
                  <td className="p-2">
                    <div className="w-10 h-10 rounded bg-black border border-white/5 overflow-hidden flex items-center justify-center">
                      {p.image ? (
                        <img
                          src={p.image}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <ImageIcon size={14} className="text-white/30" />
                      )}
                    </div>
                  </td>
                  <td className="p-2 text-white max-w-[260px] truncate" title={p.title}>
                    {p.title}
                  </td>
                  <td className="p-2 font-mono text-[11px] text-lifted-dim max-w-[160px] truncate">
                    {p.handle}
                  </td>
                  <td className="p-2 text-lifted-green">{fmtUsd(p.price_cents)}</td>
                  <td className="p-2">
                    <select
                      value={p.section || ""}
                      onChange={(e) =>
                        patchProduct(p.handle, { section: e.target.value || null })
                      }
                      className="bg-black/40 border border-white/10 text-white text-xs rounded px-2 py-1 focus:outline-none focus:border-lifted-green"
                      data-testid={`section-select-${p.handle}`}
                    >
                      <option value="">unclassified</option>
                      <option value="gear">gear</option>
                      <option value="accessories">accessories</option>
                      <option value="program">program</option>
                    </select>
                  </td>
                  <FlagCell
                    handle={p.handle}
                    field="featured"
                    value={p.featured}
                    onToggle={patchProduct}
                  />
                  <FlagCell
                    handle={p.handle}
                    field="new_release"
                    value={p.new_release}
                    onToggle={patchProduct}
                  />
                  <FlagCell
                    handle={p.handle}
                    field="best_seller"
                    value={p.best_seller}
                    onToggle={patchProduct}
                  />
                  <FlagCell
                    handle={p.handle}
                    field="same_day_eligible"
                    value={p.same_day_eligible}
                    onToggle={patchProduct}
                  />
                  <td className="p-2 text-xs text-lifted-dim">
                    {p.collection_slug || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[10.5px] text-lifted-dim mt-4 leading-relaxed">
        Changes write directly to the migrated catalog. The public storefront
        (`/`, `/shop`, `/collections/lifted-gear`, `/collections/lifted-accessories`)
        reads from the same rows — your edits go live the moment the toggle saves.
      </p>
    </div>
  );
}

function Stat({ label, value, sub, tone, mono }) {
  const toneClass = tone === "warn" ? "text-amber-300" : "text-white";
  return (
    <div className="glass-panel p-3" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="text-[10px] uppercase tracking-wider text-lifted-dim">{label}</p>
      <p className={`font-anton text-xl ${toneClass} ${mono ? "font-mono normal-case" : ""}`}>
        {value ?? "—"}
      </p>
      {sub && <p className="text-[10px] text-lifted-dim mt-0.5">{sub}</p>}
    </div>
  );
}

function FlagCell({ handle, field, value, onToggle }) {
  return (
    <td className="p-2 text-center">
      <button
        onClick={() => onToggle(handle, { [field]: !value })}
        className={`w-7 h-7 rounded-full border flex items-center justify-center mx-auto ${
          value
            ? "bg-lifted-green/25 border-lifted-green text-lifted-green"
            : "bg-black/30 border-white/10 text-white/30 hover:border-white/30"
        }`}
        data-testid={`flag-${field}-${handle}`}
        aria-pressed={value}
        title={value ? "On — click to disable" : "Off — click to enable"}
      >
        {value ? <CheckCircle2 size={14} /> : <span className="text-[10px]">—</span>}
      </button>
    </td>
  );
}
